import { useEffect, useRef, useState, createContext, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ServicesHeroThreads } from "@/components/ServicesHeroThreads";
import { ArrowRight, CheckCircle2, Sparkles, X } from "lucide-react";
import Lottie from "lottie-react";
import codingAnimation from "@/data/Coding.json";
import robotAnimation from "@/data/Robot assistant  Online manager.json";
import laptopAnimation from "@/data/Laptop Floating Animation w Servers.json";
import customerSupportAnimation from "@/data/Customer Support.json";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
} from "@/components/ui/carousel";
import {
  ServiceCategory,
  Service,
} from "@/data/servicesData";
import { ServicesHeader } from "@/components/ServicesHeader";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { serviceCategoryCopy } from "@/lib/siteCopy";
import {
  getLocalizedCategoryBySlug,
  getLocalizedAllCategories,
} from "@/lib/localizeServices";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

// Service images mapping - using online images
const serviceImages: Record<string, string[]> = {
  // Web & App Development
  "web-development": [
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80",
    "https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80",
  ],
  "full-stack-web-apps": [
    "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80",
    "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80",
    "https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800&q=80",
  ],
  "website-customization": [
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
    "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80",
    "https://images.unsplash.com/photo-1522542550221-31fd8575f4a7?w=800&q=80",
  ],
  "android-app-development": [
    "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80",
    "https://images.unsplash.com/photo-1526498460520-4c246339dccb?w=800&q=80",
    "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80",
  ],
  "apis-integrations": [
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80",
    "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80",
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80",
  ],
  // AI & Automation
  "ai-ml-solutions": [
    "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
    "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80",
    "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80",
  ],
  "ai-technology-consulting": [
    "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
    "https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=800&q=80",
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80",
  ],
  "ai-agents": [
    "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800&q=80",
    "https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800&q=80",
    "https://images.unsplash.com/photo-1507146153580-69a1fe6d8aa1?w=800&q=80",
  ],
  "ai-fine-tuning": [
    "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
    "https://images.unsplash.com/photo-1509966756634-9c23dd6e6815?w=800&q=80",
    "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=800&q=80",
  ],
  "ai-chatbot": [
    "https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=800&q=80",
    "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800&q=80",
    "https://images.unsplash.com/photo-1676299081847-824916de030a?w=800&q=80",
  ],
  "rules-based-chatbot": [
    "https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=800&q=80",
    "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?w=800&q=80",
    "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&q=80",
  ],
  "automations-agents": [
    "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
    "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80",
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
  ],
  nlp: [
    "https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=800&q=80",
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80",
    "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=800&q=80",
  ],
  // DevOps & Cloud
  "devops-cloud-services": [
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80",
    "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80",
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80",
  ],
  "speed-optimization": [
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80",
  ],
  // Support & Maintenance
  "maintenance-support": [
    "https://images.unsplash.com/photo-1581472723648-909f4851d4ae?w=800&q=80",
    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
  ],
  "bug-fixes": [
    "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80",
    "https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=800&q=80",
    "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=800&q=80",
  ],
  "website-customization-support": [
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
    "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80",
    "https://images.unsplash.com/photo-1522542550221-31fd8575f4a7?w=800&q=80",
  ],
  "performance-optimization": [
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80",
  ],
};

// Default images for services without specific images
const defaultServiceImages = [
  "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80",
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80",
];

// Context for managing drawer state
interface ServiceDrawerContextType {
  openDrawer: (service: Service) => void;
  closeDrawer: () => void;
  selectedService: Service | null;
  isOpen: boolean;
}

const ServiceDrawerContext = createContext<
  ServiceDrawerContextType | undefined
>(undefined);

export function useServiceDrawer() {
  const context = useContext(ServiceDrawerContext);
  if (!context) {
    throw new Error(
      "useServiceDrawer must be used within a ServiceDrawerProvider",
    );
  }
  return context;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
    },
  },
};

const scaleVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut" as const,
    },
  },
};

// Animated Section Component
function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Glass Card Component
function GlassCard({
  children,
  className = "",
  hover = true,
  onClick,
}: {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  onClick?: () => void;
}) {
  return (
    <motion.div
      variants={itemVariants}
      whileHover={hover ? { y: -8, scale: 1.02 } : {}}
      whileTap={hover ? { scale: 0.98 } : {}}
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-2xl
        bg-white/60 dark:bg-slate-900/60
        backdrop-blur-xl backdrop-saturate-150
        border border-white/20 dark:border-slate-700/50
        shadow-[0_8px_32px_rgba(0,0,0,0.08)]
        dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]
        transition-all duration-500 ease-out
        ${hover ? "cursor-pointer hover:shadow-[0_20px_60px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] hover:border-white/40 dark:hover:border-slate-600/70" : ""}
        ${className}
      `}
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent dark:from-white/5 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

// Service Card Component
function ServiceCard({
  service,
  categorySlug,
  index,
}: {
  service: Service;
  categorySlug: string;
  index: number;
}) {
  const Icon = service.icon;
  const { openDrawer } = useServiceDrawer();
  const lang = useSiteLanguage();
  const t = serviceCategoryCopy[lang];

  return (
    <GlassCard onClick={() => openDrawer(service)} className="group p-6">
      {/* Light blue hover effect */}
      <div className="absolute -inset-px rounded-2xl bg-blue-100/40 dark:bg-blue-900/20 transition-all duration-500 opacity-0 group-hover:opacity-100" />

      <div className="relative">
        {/* Icon with gradient background */}
        <div className="mb-4 relative">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
            <Icon className="w-7 h-7 text-slate-700 dark:text-slate-300" />
          </div>
          {/* Floating sparkle */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
            className="absolute -top-1 -right-1"
          >
            <Sparkles className="w-4 h-4 text-cyan-500" />
          </motion.div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
          {service.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {service.tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="bg-slate-100/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 text-xs"
            >
              {tag}
            </Badge>
          ))}
        </div>

        {/* Learn more link */}
        <div className="flex items-center gap-2 text-sm font-medium text-cyan-600 dark:text-cyan-400 group-hover:gap-3 transition-all">
          <span>{t.learnMore}</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </GlassCard>
  );
}

// Service Detail Drawer Component
function ServiceDetailDrawer() {
  const { selectedService, isOpen, closeDrawer } = useServiceDrawer();
  const navigate = useNavigate();
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const lang = useSiteLanguage();
  const t = serviceCategoryCopy[lang];
  const category = categorySlug
    ? getLocalizedCategoryBySlug(categorySlug, lang)
    : undefined;

  if (!selectedService) return null;

  const Icon = selectedService.icon;
  const images = serviceImages[selectedService.id] || defaultServiceImages;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeDrawer()}>
      <DrawerContent className="max-h-[90vh] md:max-h-[85vh]">
        <div className="mx-auto w-full max-w-6xl relative">
          <DrawerClose
            className="absolute right-3 top-3 z-20 rounded-full p-2 bg-background/80 border border-border hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </DrawerClose>
          {/* Main Content - Scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] md:max-h-[calc(85vh-120px)]">
            {/* Header Section */}
            <div className="px-4 md:px-8 pt-4 pb-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, type: "spring" }}
                  className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex-shrink-0"
                >
                  <Icon className="h-7 w-7 md:h-8 md:w-8 text-blue-600 dark:text-blue-400" />
                </motion.div>

                {/* Title and Description */}
                <div className="flex-1">
                  <DrawerTitle className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">
                    {selectedService.title}
                  </DrawerTitle>
                  <DrawerDescription className="text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                    {selectedService.longDescription}
                  </DrawerDescription>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {selectedService.tags.map((tag) => (
                      <Badge
                        key={tag}
                        className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 border border-blue-200 dark:border-blue-800 text-xs"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 px-4 md:px-8 pb-6">
              {/* Left Side - Image Carousel */}
              <div className="order-1 md:order-2">
                <div className="relative rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden bg-gray-50 dark:bg-gray-900/50">
                  <Carousel autoplay autoplayDelay={4000} className="w-full">
                    <CarouselContent>
                      {images.map((image, index) => (
                        <CarouselItem key={index}>
                          <div className="aspect-[4/3] w-full">
                            <img
                              src={image}
                              alt={`${selectedService.title} - Image ${index + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselDots className="pb-4" />
                  </Carousel>
                </div>
              </div>

              {/* Right Side - Features */}
              <div className="order-2 md:order-1 space-y-4">
                <h4 className="text-base md:text-lg font-bold text-slate-900 dark:text-white">
                  {t.whatsIncluded}
                </h4>
                <div className="space-y-2 md:space-y-3">
                  {selectedService.features.map((feature, index) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="flex items-start gap-3 p-3 md:p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800"
                    >
                      <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm md:text-base text-slate-700 dark:text-slate-300">
                        {feature}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-800 px-4 md:px-8 py-4 md:py-6 flex flex-col sm:flex-row gap-3 justify-end bg-white dark:bg-background">
            <Button
              size="lg"
              className="w-full sm:w-auto px-8 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-gray-100 text-white dark:text-slate-900 shadow-sm transition-all duration-300 font-semibold"
              onClick={() => {
                closeDrawer();
                setTimeout(() => {
                  navigate("/contact", {
                    state: {
                      service: selectedService.title,
                      category: category?.title,
                      serviceId: selectedService.id,
                      query: `Get Started · ${selectedService.title}`,
                      message: t.enquireAbout.replace(
                        "{service}",
                        selectedService.title,
                      ),
                    },
                  });
                }, 300);
              }}
            >
              {t.getStarted}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// Hero Section
function CategoryHero({ category }: { category: ServiceCategory }) {
  const Icon = category.icon;
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = serviceCategoryCopy[lang];
  // Select animation based on category id
  const animationMap: Record<string, any> = {
    "web-app": codingAnimation,
    "ai-automation": robotAnimation,
    "devops-cloud": laptopAnimation,
    "support-maintenance": customerSupportAnimation,
  };
  const chosenAnimation = animationMap[category.id] ?? codingAnimation;

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <ServicesHeroThreads />

      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden z-[1] pointer-events-none">
        {/* Gradient orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-br ${category.gradient} blur-3xl`}
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gradient-to-tr ${category.gradient} blur-3xl`}
        />

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          {/* Lottie animation (moved to right on md+) */}
          <div className="flex items-center justify-center md:order-last">
            <div className="w-64 h-64 md:w-96 md:h-96">
              <Lottie
                animationData={chosenAnimation}
                loop={true}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          </div>

          {/* Right: Category content */}
          <div className="max-w-4xl">
            {/* Category icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, type: "spring", bounce: 0.4 }}
              className="mb-6"
            >
              <div
                className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${category.gradient} backdrop-blur-xl border border-white/20 dark:border-slate-700/50 flex items-center justify-center shadow-lg`}
              >
                <Icon className="w-10 h-10 text-slate-700 dark:text-slate-200" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6"
            >
              {category.title}
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="text-xl text-slate-600 dark:text-slate-400 mb-8 leading-relaxed"
            >
              {category.longDescription}
            </motion.p>

            {/* Quick stats or badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-wrap gap-3"
            >
              <Badge
                variant="secondary"
                className="px-4 py-2 text-sm bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm"
              >
                {t.servicesBadge.replace(
                  "{count}",
                  String(category.services.length),
                )}
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 text-sm bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm"
              >
                {t.enterpriseReady}
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 text-sm bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm"
              >
                {t.support247}
              </Badge>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Category Navigation
function CategoryNavigation({ currentCategory }: { currentCategory: string }) {
  const lang = useSiteLanguage();
  const categories = getLocalizedAllCategories(lang);
  const navigate = useNavigate();

  return (
    <section className="py-8 border-y border-slate-200/50 dark:border-slate-800/50 bg-white/30 dark:bg-slate-900/30 backdrop-blur-sm sticky top-14 z-40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={category.id === currentCategory ? "default" : "ghost"}
              onClick={() => navigate(`/services/${category.id}`)}
              className={`whitespace-nowrap ${
                category.id === currentCategory
                  ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                  : "hover:bg-white/50 dark:hover:bg-slate-800/50"
              }`}
            >
              {category.title}
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section removed per request

// Main Category Page Component
export function ServiceCategoryPage({
  navItems,
  onMobileMenuOpen,
}: {
  navItems: NavItem[];
  onMobileMenuOpen?: () => void;
}) {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = serviceCategoryCopy[lang];

  // Drawer state management
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const category = categorySlug
    ? getLocalizedCategoryBySlug(categorySlug, lang)
    : undefined;

  const openDrawer = (service: Service) => {
    setSelectedService(service);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    // Clear hash so the open-from-hash effect cannot reopen the drawer
    if (location.hash) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
    // Keep selectedService for exit animation
    setTimeout(() => setSelectedService(null), 300);
  };

  // Open drawer once when navigating with a service hash (e.g. from search)
  useEffect(() => {
    if (!location.hash || !categorySlug) return;

    const serviceId = location.hash.replace("#", "");
    const cat = getLocalizedCategoryBySlug(categorySlug, lang);
    const service = cat?.services.find((s) => s.id === serviceId);
    if (!service) return;

    const timer = window.setTimeout(() => {
      setSelectedService(service);
      setIsDrawerOpen(true);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [location.hash, categorySlug, lang]);

  // Initial scroll when landing without a deep-link hash
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Only on category change / first mount — not on hash clear after close
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            {t.notFound}
          </h1>
          <Button onClick={() => navigate("/services")}>
            {t.backToServices}
          </Button>
        </div>
      </div>
    );
  }

  const drawerContextValue: ServiceDrawerContextType = {
    openDrawer,
    closeDrawer,
    selectedService,
    isOpen: isDrawerOpen,
  };

  return (
    <ServiceDrawerContext.Provider value={drawerContextValue}>
      <div className="min-h-screen bg-background">
        <ServicesHeader
          navItems={navItems}
          onMobileMenuOpen={onMobileMenuOpen}
        />

        <CategoryHero category={category} />

        {/* Category Navigation */}
        <CategoryNavigation currentCategory={category.id} />

        {/* Services Overview Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <AnimatedSection>
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">
                  {t.ourServices.replace("{category}", category.title)}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                  {t.clickHint}
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.services.map((service, index) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    categorySlug={category.id}
                    index={index}
                  />
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Service Detail Drawer */}
        <ServiceDetailDrawer />

        {/* CTA Section removed per request */}
      </div>
    </ServiceDrawerContext.Provider>
  );
}
